import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { nb } from 'date-fns/locale';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try { return format(parseISO(dateStr), "d. MMM, HH:mm", { locale: nb }); }
  catch { return ''; }
};

const ReplyNotificationsPanel = ({ replies, loading, onMarkRead }) => {
  const [expanded, setExpanded] = useState(true);
  const unreadCount = replies?.filter(r => !r?._read)?.length || 0;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(231,111,81,0.25)',
      position: 'relative',
      overflow: 'hidden',
      transform: 'rotate(0.3deg)',
    }}>
      {/* Diagonal accent */}
      <div style={{ position: 'absolute', bottom: 0, right: 0, width: '60px', height: '60px', background: 'rgba(231,111,81,0.06)', clipPath: 'polygon(100% 0, 0 100%, 100% 100%)' }} />
      {/* Header */}
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E76F51" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-8px',
                background: '#E76F51', color: '#EEF0F2',
                fontFamily: 'Impact, sans-serif', fontSize: '0.65rem',
                width: '18px', height: '18px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1,
              }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </div>
          <h3 style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.1rem', color: '#EEF0F2', letterSpacing: '0.04em' }}>SVAR-VARSLER</h3>
          {unreadCount > 0 && (
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: '#E76F51', border: '1px solid #E76F5144', padding: '2px 8px', letterSpacing: '0.1em' }}>
              {unreadCount} ULEST
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {unreadCount > 0 && (
            <button
              onClick={e => { e?.stopPropagation(); onMarkRead?.(); }}
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: '#2EC4A0', background: 'none', border: '1px solid #2EC4A044', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.1em' }}
            >
              MERK ALLE LEST
            </button>
          )}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(238,240,242,0.4)" strokeWidth="2" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>
      {expanded && (
        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: 'rgba(238,240,242,0.3)' }}>LASTER...</div>
          ) : replies?.length === 0 ? (
            <div style={{ padding: '32px 24px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '0.85rem', color: 'rgba(238,240,242,0.35)', fontStyle: 'italic' }}>Ingen svar ennå</div>
            </div>
          ) : (
            replies?.map((reply, i) => (
              <div
                key={reply?.id}
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: !reply?._read ? 'rgba(231,111,81,0.04)' : 'transparent',
                  display: 'flex', gap: '12px', alignItems: 'flex-start',
                }}
              >
                {/* Unread dot */}
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: !reply?._read ? '#E76F51' : 'transparent', border: !reply?._read ? 'none' : '1px solid rgba(255,255,255,0.1)', flexShrink: 0, marginTop: '4px' }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.8rem', color: '#EEF0F2' }}>{reply?.author_name}</span>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: 'rgba(238,240,242,0.35)' }}>svarte på din kommentar</span>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: 'rgba(238,240,242,0.25)' }}>{formatDate(reply?.created_at)}</span>
                  </div>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.78rem', color: 'rgba(238,240,242,0.6)', fontStyle: 'italic', margin: 0, lineHeight: 1.5 }}>
                    {reply?.content?.length > 100 ? `${reply?.content?.slice(0, 100)}…` : reply?.content}
                  </p>
                  {reply?.post_id && (
                    <Link
                      to={`/individual-article-detail-view?id=${reply?.post_id}`}
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: '#3B6FD4', textDecoration: 'none', display: 'inline-block', marginTop: '6px', letterSpacing: '0.05em' }}
                    >
                      → SE ARTIKKEL
                    </Link>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ReplyNotificationsPanel;
